<template>
  <div>
    <master-crud-page
      master="notification/subscription"
      title="Notification Subscription"
      access="master_notification_subscription"
      pk="ntsbId"
      :columns="columns"
      :fields="fields"
      :filters="filters"
      :embedded="embedded"
      page-size-selectable
      forbidden-text="This module is internal only."
    >
      <template #actions>
        <b-button size="sm" variant="outline-primary" class="mr-2" @click="showPreview = true">
          Preview recipients
        </b-button>
      </template>
    </master-crud-page>
    <phase-legend :note="subscriptionPhaseNote" />
    <notification-preview-modal v-model="showPreview" />
  </div>
</template>

<script>
import MasterCrudPage from '@/components/master/MasterCrudPage';
import NotificationPreviewModal from '@/components/notification/NotificationPreviewModal';
import PhaseLegend from '@/components/notification/PhaseLegend';
import {
  CATEGORY_OPTIONS, SEVERITY_OPTIONS, PHASE_OPTIONS, CHANNEL_OPTIONS,
  SCOPE_TYPE_OPTIONS, AUDIENCE_TYPE_OPTIONS, STATUS_OPTIONS, isCategoryTarget,
  TOPIC_SOURCE, ASSET_SOURCE, USER_SOURCE, statusColumn, relationOf,
} from '@/util/notificationConfig';

const CONTACT_SOURCE = { master: 'notification/contact', keyField: 'ntcnId', displayField: 'ntcnName' };

const TARGET_TYPE_OPTIONS = [
  { value: 'topic', text: 'Specific topic' },
  { value: 'category', text: 'Whole category' },
];

const SUBSCRIPTION_PHASE_NOTE = 'Tick only the phases you want to receive. For example, Start and Resolve '
  + 'without Reminder gives you the outage and the recovery, with no repeated reminders in between.';

export default {
  name: 'MasterNotificationSubscription',
  components: { MasterCrudPage, NotificationPreviewModal, PhaseLegend },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      showPreview: false,
      subscriptionPhaseNote: SUBSCRIPTION_PHASE_NOTE,
      columns: [
        {
          key: 'ntsbScopeType',
          label: 'Scope',
          align: 'left',
          formatter: (value, key, row) => (value === 'ASSET' ? `ASSET - ${row.ntsbMassId}` : value),
        },
        {
          key: 'ntsbNtopId',
          label: 'Target',
          align: 'left',
          formatter: (value, key, row) => {
            const topic = relationOf(row.ntsbNtop);
            if (topic) return topic.ntopName;
            return row.ntsbCategory ? `Category: ${row.ntsbCategory}` : '-';
          },
        },
        {
          key: 'ntsbAudienceType',
          label: 'Audience',
          align: 'left',
          formatter: (value, key, row) => {
            if (value === 'USER') return `USER - ${row.ntsbUserId}`;
            if (value === 'CONTACT') {
              const contact = relationOf(row.ntsbNtcn);
              return `CONTACT - ${contact ? contact.ntcnName : row.ntsbNtcnId}`;
            }
            return 'MEMBERS';
          },
        },
        {
          key: 'ntsbChannel', label: 'Channel', type: 'badge', variant: () => 'secondary',
        },
        {
          key: 'ntsbMinSeverity',
          label: 'Severity & Phases',
          align: 'left',
          formatter: (value, key, row) => `min ${value} - ${row.ntsbPhases}`,
        },
        statusColumn('ntsbEnabled', 'Status'),
      ],
      fields: [
        {
          key: 'ntsbScopeType', label: 'Scope', type: 'select', required: true, options: SCOPE_TYPE_OPTIONS, default: 'COMPANY',
        },
        {
          key: 'ntsbMassId',
          label: 'Asset',
          type: 'select',
          searchable: true,
          source: ASSET_SOURCE,
          visibleWhen: (form) => form.ntsbScopeType === 'ASSET',
        },
        {
          key: 'targetType',
          label: 'Target',
          type: 'select',
          virtual: true,
          options: TARGET_TYPE_OPTIONS,
        },
        {
          key: 'ntsbNtopId',
          label: 'Topic',
          type: 'select',
          searchable: true,
          source: TOPIC_SOURCE,
          visibleWhen: (form) => !isCategoryTarget(form),
        },
        {
          key: 'ntsbCategory',
          label: 'Category',
          type: 'select',
          options: CATEGORY_OPTIONS,
          visibleWhen: (form) => isCategoryTarget(form),
        },
        {
          key: 'ntsbAudienceType', label: 'Audience', type: 'select', required: true, options: AUDIENCE_TYPE_OPTIONS, default: 'MEMBERS',
        },
        {
          key: 'ntsbUserId',
          label: 'User',
          type: 'select',
          searchable: true,
          source: USER_SOURCE,
          visibleWhen: (form) => form.ntsbAudienceType === 'USER',
        },
        {
          key: 'ntsbNtcnId',
          label: 'Contact',
          type: 'select',
          searchable: true,
          source: CONTACT_SOURCE,
          visibleWhen: (form) => form.ntsbAudienceType === 'CONTACT',
        },
        {
          key: 'ntsbChannel',
          label: 'Channel',
          type: 'select',
          required: true,
          default: 'inapp',
          options: (form) => (form.ntsbAudienceType === 'CONTACT'
            ? CHANNEL_OPTIONS.filter((option) => option.value === 'email')
            : CHANNEL_OPTIONS),
          hint: 'A contact subscription can only use email.',
        },
        {
          key: 'ntsbMinSeverity', label: 'Minimum Severity', type: 'select', options: SEVERITY_OPTIONS, default: 'INFO',
        },
        {
          key: 'ntsbPhases',
          label: 'Phases',
          type: 'checkbox-group',
          options: PHASE_OPTIONS,
          default: 'START,REMINDER,RESOLVE,SINGLE',
          colMd: 12,
        },
        {
          key: 'ntsbEnabled', label: 'Status', type: 'select', options: STATUS_OPTIONS, default: 1,
        },
      ],
      filters: [
        {
          key: 'ntsbScopeType', label: 'Scope', type: 'select', options: SCOPE_TYPE_OPTIONS, colMd: 3,
        },
        {
          key: 'ntsbChannel', label: 'Channel', type: 'select', options: CHANNEL_OPTIONS, colMd: 3,
        },
        {
          key: 'ntsbAudienceType', label: 'Audience', type: 'select', options: AUDIENCE_TYPE_OPTIONS, colMd: 3,
        },
        {
          key: 'ntsbNtopId', label: 'Topic', type: 'select', source: TOPIC_SOURCE, searchable: true, colMd: 3,
        },
      ],
    };
  },
};
</script>
